import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdSixtySixSidesDiagramComponent } from './hd-sixty-six-sides-diagram.component';
import { HD_FIXED_STARS, HdFixedStarEffect } from './hd-fixed-stars.metadata';
import { HD_CONSTELLATION_NAMES, HD_FIXED_STAR_SKY } from './hd-fixed-star-sky.data';
// De service opent dit paneel én dit paneel opent via de service de ster-panelen:
// een bewuste, runtime-veilige module-cycle (zie ook hd-fixed-star-panel.component.ts).
import { HdFixedStarsPanelService } from './hd-fixed-stars-panel.service';

interface GeneralInfoItemVm {
  readonly label: string;
  readonly text: string;
}

interface GeneralInfoSplitBranchVm {
  readonly title: string;
  readonly subtitle: string;
  readonly points: readonly string[];
}

/** Vertakkingsschema: één bron die zich in twee (of meer) takken splitst. */
interface GeneralInfoSplitVm {
  readonly root: string;
  readonly branches: readonly GeneralInfoSplitBranchVm[];
}

interface GeneralInfoSectionVm {
  readonly title: string;
  readonly lead: readonly string[];
  readonly numbered: boolean;
  /** Gekaderde notitie-sectie (praktijkvoorbeelden e.d.). */
  readonly isNote: boolean;
  readonly flow: readonly string[];
  readonly split: GeneralInfoSplitVm | null;
  readonly items: readonly GeneralInfoItemVm[];
  /** Afsluitende alinea('s) na de items. */
  readonly outro: readonly string[];
}

interface GeneralInfoVm {
  readonly title: string;
  readonly intro: readonly string[];
  readonly sections: readonly GeneralInfoSectionVm[];
  readonly summaryTitle: string;
  readonly summary: readonly string[];
  readonly methodTitle: string;
  readonly methodParagraphs: readonly string[];
}

interface StarRowVm {
  readonly side: number;
  readonly name: string;
  readonly constellation: string;
  readonly effectLabel: string;
  readonly effectColor: string;
}

interface StarListVm {
  readonly title: string;
  readonly intro: string;
  readonly compactLabel: string;
  readonly richLabel: string;
  readonly colName: string;
  readonly colConstellation: string;
  readonly colEffect: string;
  readonly rows: readonly StarRowVm[];
}

const EFFECT_COLORS: Record<HdFixedStarEffect, string> = {
  veryFortunate: '#2e7d32',
  fortunate: '#4c8c4a',
  neutral: '#8a8a88',
  unfortunate: '#c1662f',
  veryUnfortunate: '#b23b3b',
};

// Ruime guards tegen oneindige lussen; de echte grens is de eerste lege
// titel/label. Ruim boven de content houden: een te krappe cap kapt secties
// stilletjes af (dat gebeurde op 2026-09-22 bij 15 secties met cap 12).
const MAX_SECTIONS = 40;
const MAX_ITEMS = 16;

/**
 * Algemene info-pagina over de fixed stars en de 66 Sides: de kosmologische
 * achtergrond uit de Bhan Tugh (neutrino-oceaan, Shattering, Centrum, Camel &
 * Dog, 4 Corners, 16 Faces, 66 Sides), de duiding van de tekening en, onder
 * een eigen titel, de berekening (hergebruikt `panel.method.paragraphs`).
 * Teksten uit de `human-design/fixed-stars`-scope (`generalInfo.*`),
 * content-taal met NL-fallback. Geopend via `HdFixedStarsPanelService`.
 */
@Component({
  selector: 'hd-fixed-stars-general-info',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdSixtySixSidesDiagramComponent],
  template: `
    <section class="fixed-stars-general-info">
      <h3 class="fixed-stars-general-info__title">{{ vm().title }}</h3>

      <section class="fixed-stars-general-info__diagram">
        <hd-sixty-six-sides-diagram [ariaLabel]="'66 Sides'" />
      </section>

      @for (paragraph of vm().intro; track $index) {
        <p class="fixed-stars-general-info__paragraph">{{ paragraph }}</p>
      }

      @for (section of vm().sections; track section.title) {
        <section class="fixed-stars-general-info__section"
                 [class.fixed-stars-general-info__section--note]="section.isNote">
          <h4>{{ section.title }}</h4>
          @for (paragraph of section.lead; track $index) {
            <p class="fixed-stars-general-info__paragraph">{{ paragraph }}</p>
          }
          @if (section.split; as split) {
            <div class="fixed-stars-general-info__split">
              <span class="fixed-stars-general-info__flow-step">{{ split.root }}</span>
              <div class="fixed-stars-general-info__split-arrows" aria-hidden="true">
                @for (branch of split.branches; track $index) {
                  <span>↓</span>
                }
              </div>
              <div class="fixed-stars-general-info__split-branches">
                @for (branch of split.branches; track branch.title) {
                  <div class="fixed-stars-general-info__split-branch">
                    <span class="fixed-stars-general-info__split-branch-title">{{ branch.title }}</span>
                    <span class="fixed-stars-general-info__split-branch-subtitle">{{ branch.subtitle }}</span>
                    <ul class="fixed-stars-general-info__split-points">
                      @for (point of branch.points; track $index) {
                        <li>{{ point }}</li>
                      }
                    </ul>
                  </div>
                }
              </div>
            </div>
          }
          @if (section.flow.length > 0) {
            <div class="fixed-stars-general-info__flow">
              @for (step of section.flow; track $index) {
                @if ($index > 0) {
                  <span class="fixed-stars-general-info__flow-arrow" aria-hidden="true">↓</span>
                }
                <span class="fixed-stars-general-info__flow-step">{{ step }}</span>
              }
            </div>
          }
          @if (section.items.length > 0) {
            @if (section.numbered) {
              <ol class="fixed-stars-general-info__items">
                @for (item of section.items; track item.label) {
                  <li><strong>{{ item.label }}:</strong> {{ item.text }}</li>
                }
              </ol>
            } @else {
              <ul class="fixed-stars-general-info__items">
                @for (item of section.items; track item.label) {
                  <li><strong>{{ item.label }}:</strong> {{ item.text }}</li>
                }
              </ul>
            }
          }
          @for (paragraph of section.outro; track $index) {
            <p class="fixed-stars-general-info__paragraph">{{ paragraph }}</p>
          }
        </section>
      }

      <section class="fixed-stars-general-info__section">
        <h4>{{ vm().summaryTitle }}</h4>
        @for (paragraph of vm().summary; track $index) {
          <p class="fixed-stars-general-info__paragraph">{{ paragraph }}</p>
        }
      </section>

      <section class="fixed-stars-general-info__section">
        <h4>{{ vm().methodTitle }}</h4>
        @for (paragraph of vm().methodParagraphs; track $index) {
          <p class="fixed-stars-general-info__paragraph">{{ paragraph }}</p>
        }
      </section>

      <section class="fixed-stars-general-info__star-list">
        <div class="star-list__head">
          <h4>{{ starList().title }}</h4>
          <div class="star-list__toggle" role="group" [attr.aria-label]="starList().title">
            <button type="button" class="star-list__toggle-btn"
                    [class.star-list__toggle-btn--active]="listMode() === 'compact'"
                    (click)="setListMode('compact')">{{ starList().compactLabel }}</button>
            <button type="button" class="star-list__toggle-btn"
                    [class.star-list__toggle-btn--active]="listMode() === 'rich'"
                    (click)="setListMode('rich')">{{ starList().richLabel }}</button>
          </div>
        </div>
        @if (starList().intro) {
          <p class="fixed-stars-general-info__paragraph">{{ starList().intro }}</p>
        }

        @if (listMode() === 'compact') {
          <ul class="star-list__compact">
            @for (row of starList().rows; track row.side) {
              <li>
                <button type="button" class="star-list__row" (click)="openStar(row.side, $event)">
                  <span class="star-list__side">{{ row.side }}</span>
                  <span class="star-list__name">{{ row.name }}</span>
                  <span class="star-list__con">{{ row.constellation }}</span>
                </button>
              </li>
            }
          </ul>
        } @else {
          <table class="star-list__table">
            <thead>
              <tr>
                <th class="star-list__num">#</th>
                <th>{{ starList().colName }}</th>
                <th>{{ starList().colConstellation }}</th>
                <th aria-hidden="true"></th>
              </tr>
            </thead>
            <tbody>
              @for (row of starList().rows; track row.side) {
                <tr class="star-list__trow" (click)="openStar(row.side, $event)">
                  <td class="star-list__num">{{ row.side }}</td>
                  <td class="star-list__name">{{ row.name }}</td>
                  <td class="star-list__con">{{ row.constellation }}</td>
                  <td class="star-list__go" aria-hidden="true"><i class="fas fa-chevron-right"></i></td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>
    </section>
  `,
  styles: `
    .fixed-stars-general-info {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 14px 16px 18px;
      max-width: min(560px, 90vw);
      color: #3d414e;
    }

    .fixed-stars-general-info__title {
      margin: 0;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 20px;
    }

    .fixed-stars-general-info__diagram {
      padding: 8px;
      background: #fafafa;
      border: 1px solid #e6e6e6;
      border-radius: 8px;
    }

    .fixed-stars-general-info__section {
      display: flex;
      flex-direction: column;
      gap: 6px;

      h4 {
        margin: 6px 0 0;
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
      }
    }

    .fixed-stars-general-info__paragraph {
      margin: 0;
      font-size: 12.5px;
      line-height: 1.5;
    }

    .fixed-stars-general-info__items {
      margin: 0;
      padding-left: 20px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 12.5px;
      line-height: 1.5;
    }

    .fixed-stars-general-info__flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      margin: 4px 0;
    }

    .fixed-stars-general-info__flow-step {
      padding: 4px 12px;
      border: 1px solid #d9cdb4;
      border-radius: 8px;
      background: #f7f3ea;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
    }

    .fixed-stars-general-info__flow-arrow {
      font-size: 12px;
      opacity: 0.55;
      line-height: 1;
    }

    .fixed-stars-general-info__section--note {
      padding: 10px 12px;
      border: 1px solid #d9cdb4;
      border-radius: 8px;
      background: #fbf8f1;

      h4 {
        margin-top: 0;
      }
    }

    .fixed-stars-general-info__split {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      margin: 4px 0;
    }

    .fixed-stars-general-info__split-arrows {
      display: flex;
      width: 100%;

      span {
        flex: 1;
        text-align: center;
        font-size: 12px;
        opacity: 0.55;
        line-height: 1.2;
      }
    }

    .fixed-stars-general-info__split-branches {
      display: flex;
      gap: 10px;
      width: 100%;
      align-items: stretch;
      flex-wrap: wrap;
    }

    .fixed-stars-general-info__split-branch {
      flex: 1 1 180px;
      display: flex;
      flex-direction: column;
      gap: 3px;
      padding: 8px 10px;
      border: 1px solid #d9cdb4;
      border-radius: 8px;
      background: #fdfbf6;
    }

    .fixed-stars-general-info__split-branch-title {
      font-size: 12px;
      font-weight: 700;
    }

    .fixed-stars-general-info__split-branch-subtitle {
      font-size: 11.5px;
      opacity: 0.7;
    }

    .fixed-stars-general-info__split-points {
      margin: 2px 0 0;
      padding-left: 16px;
      display: flex;
      flex-direction: column;
      gap: 3px;
      font-size: 12px;
      line-height: 1.45;
    }

    .fixed-stars-general-info__star-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .fixed-stars-general-info__star-list h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.7;
    }

    .star-list__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;
    }

    .star-list__toggle {
      display: inline-flex;
      border: 1px solid #d9cdb4;
      border-radius: 999px;
      overflow: hidden;
    }

    .star-list__toggle-btn {
      padding: 4px 12px;
      border: 0;
      background: #f7f3ea;
      color: inherit;
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
    }

    .star-list__toggle-btn--active {
      background: #12182c;
      color: #ffe0b4;
    }

    .star-list__compact {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(155px, 1fr));
      gap: 3px;
    }

    .star-list__row {
      display: flex;
      align-items: baseline;
      gap: 8px;
      width: 100%;
      padding: 5px 8px;
      border: 1px solid transparent;
      border-radius: 7px;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 12.5px;
      text-align: left;
      cursor: pointer;
    }

    .star-list__row:hover {
      background: #f4efe4;
      border-color: #e6dcc6;
    }

    .star-list__side {
      min-width: 20px;
      font-size: 11px;
      font-weight: 700;
      opacity: 0.55;
    }

    .star-list__name {
      font-weight: 600;
    }

    .star-list__con {
      font-size: 11.5px;
      opacity: 0.65;
    }

    .star-list__row .star-list__con {
      margin-left: auto;
    }

    .star-list__table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12.5px;
    }

    .star-list__table th {
      padding: 4px 8px;
      border-bottom: 1px solid #e6e6e6;
      font-size: 11px;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      opacity: 0.55;
    }

    .star-list__table td {
      padding: 5px 8px;
      border-bottom: 1px solid #f0efe9;
    }

    .star-list__trow {
      cursor: pointer;
    }

    .star-list__trow:hover td {
      background: #f4efe4;
    }

    .star-list__num {
      width: 26px;
      font-weight: 700;
      opacity: 0.55;
    }

    .star-list__effect {
      font-weight: 600;
    }

    .star-list__go {
      width: 22px;
      text-align: center;
      opacity: 0.4;
    }
  `,
})
export class HdFixedStarsGeneralInfoComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly panelService = inject(HdFixedStarsPanelService);

  protected readonly listMode = signal<'compact' | 'rich'>('compact');

  constructor() {
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected readonly vm = computed<GeneralInfoVm>(() => ({
    title: this.text('generalInfo.title'),
    intro: this.list('generalInfo.intro'),
    sections: this.sections(),
    summaryTitle: this.text('generalInfo.summaryTitle'),
    summary: this.list('generalInfo.summary'),
    methodTitle: this.text('generalInfo.methodTitle'),
    methodParagraphs: this.list('panel.method.paragraphs'),
  }));

  protected readonly starList = computed<StarListVm>(() => ({
    title: this.text('generalInfo.starList.title'),
    intro: this.text('generalInfo.starList.intro'),
    compactLabel: this.text('generalInfo.starList.compact'),
    richLabel: this.text('generalInfo.starList.rich'),
    colName: this.text('generalInfo.starList.colName'),
    colConstellation: this.text('generalInfo.starList.colConstellation'),
    colEffect: this.text('generalInfo.starList.colEffect'),
    rows: HD_FIXED_STARS.map(star => {
      const con = HD_FIXED_STAR_SKY[star.side]?.con;
      return {
        side: star.side,
        name: star.name,
        constellation: (con ? HD_CONSTELLATION_NAMES[con] : undefined) ?? '',
        effectLabel: star.effect ? this.text(`panel.effect.${star.effect}`) : '',
        effectColor: star.effect ? EFFECT_COLORS[star.effect] : 'inherit',
      };
    }),
  }));

  protected setListMode(mode: 'compact' | 'rich'): void {
    this.listMode.set(mode);
  }

  protected openStar(side: number, event: MouseEvent): void {
    this.panelService.openForStar(side, null, { x: event.clientX, y: event.clientY });
  }

  private sections(): GeneralInfoSectionVm[] {
    const sections: GeneralInfoSectionVm[] = [];
    for (let i = 0; i < MAX_SECTIONS; i++) {
      const base = `generalInfo.sections.${i}`;
      const title = this.text(`${base}.title`);
      if (title === '') {
        break;
      }
      sections.push({
        title,
        lead: this.list(`${base}.lead`),
        numbered: this.text(`${base}.style`) === 'numbered',
        isNote: this.text(`${base}.note`) === 'true',
        flow: this.list(`${base}.flow`),
        split: this.split(base),
        items: this.items(base),
        outro: this.list(`${base}.outro`),
      });
    }
    return sections;
  }

  private split(base: string): GeneralInfoSplitVm | null {
    const root = this.text(`${base}.split.root`);
    if (root === '') {
      return null;
    }
    const branches: GeneralInfoSplitBranchVm[] = [];
    for (let i = 0; i < 4; i++) {
      const branchBase = `${base}.split.branches.${i}`;
      const title = this.text(`${branchBase}.title`);
      if (title === '') {
        break;
      }
      branches.push({
        title,
        subtitle: this.text(`${branchBase}.subtitle`),
        points: this.list(`${branchBase}.points`),
      });
    }
    return { root, branches };
  }

  private items(base: string): GeneralInfoItemVm[] {
    const items: GeneralInfoItemVm[] = [];
    for (let i = 0; i < MAX_ITEMS; i++) {
      const label = this.text(`${base}.items.${i}.label`);
      const text = this.text(`${base}.items.${i}.text`);
      if (label === '' && text === '') {
        break;
      }
      items.push({ label, text });
    }
    return items;
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('fixedStars', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('fixedStars', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('fixedStars', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('fixedStars', key, 'nl');
  }
}
